import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, TrendingUp, Building2, CalendarCheck, ListChecks, ArrowUpRight } from "lucide-react";
import AdminHeader from "../../components/layout/AdminHeader";
import { api } from "../../lib/api";
import { useSettings } from "../../lib/settings";

const stageLabel = (key, taxonomy) => (taxonomy || []).find((x) => x.key === key)?.label || key;

function Stat({ icon: Icon, label, value, hint, testId, tone = "ivory" }) {
  return (
    <div className={`admin-stat`} data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-[0.2em] text-altura-charcoal/50">{label}</div>
        <Icon className="h-4 w-4 text-altura-gold-dark" />
      </div>
      <div className="font-editorial text-3xl text-altura-espresso">{value}</div>
      {hint && <div className="text-[11px] text-altura-charcoal/50">{hint}</div>}
    </div>
  );
}

export default function AdminDashboard() {
  const [m, setM] = useState(null);
  const settings = useSettings();
  const stages = settings?.get("pipeline_stage") || [];
  const sources = settings?.get("lead_source") || [];

  useEffect(() => {
    api.get("/dashboard/metrics").then((r) => setM(r.data)).catch(() => setM({}));
  }, []);

  return (
    <div data-testid="page-admin-dashboard">
      <AdminHeader breadcrumb="Executive Operations" title="Dashboard" subtitle="Operational overview of Altura Global's investor pipeline and portfolio." />
      <div className="p-8 space-y-8">
        {!m ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[...Array(8)].map((_, i) => <div key={i} className="h-28 rounded-md bg-white animate-pulse" />)}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Stat icon={Users} label="Total leads" value={m.total_leads ?? 0} hint={`+${m.new_leads_7d ?? 0} last 7 days`} testId="stat-total-leads" />
              <Stat icon={TrendingUp} label="Qualified investors" value={m.qualified ?? 0} testId="stat-qualified" />
              <Stat icon={Building2} label="Active opportunities" value={m.active_opportunities ?? 0} hint={`${m.featured_opportunities ?? 0} featured`} testId="stat-active-opps" />
              <Stat icon={CalendarCheck} label="Consultations pending" value={m.consultations_pending ?? 0} testId="stat-consults" />
              <Stat icon={ListChecks} label="Tasks open" value={m.tasks_open ?? 0} hint={`${m.tasks_overdue ?? 0} overdue`} testId="stat-tasks" />
              <Stat icon={Users} label="Most requested market" value={m.top_markets?.[0]?.market || "—"} testId="stat-top-market" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="admin-card">
                <div className="flex items-center justify-between">
                  <div className="font-editorial text-xl text-altura-espresso">Pipeline summary</div>
                  <Link to="/altura-command/leads" className="text-xs text-altura-gold-dark hover:text-altura-gold inline-flex items-center gap-1">Open CRM <ArrowUpRight className="h-3 w-3" /></Link>
                </div>
                <div className="mt-4 space-y-2">
                  {stages.slice(0, 8).map((s) => {
                    const c = m.pipeline_summary?.[s.key] || 0;
                    const max = Math.max(...Object.values(m.pipeline_summary || { x: 1 }));
                    return (
                      <div key={s.key} className="flex items-center gap-3 text-sm">
                        <div className="w-48 truncate text-altura-charcoal/75">{s.label}</div>
                        <div className="flex-1 h-1.5 bg-altura-beige rounded-full overflow-hidden">
                          <div className="h-full bg-altura-gold" style={{ width: `${max ? (c / max) * 100 : 0}%` }} />
                        </div>
                        <div className="w-8 text-right font-mono text-xs text-altura-espresso">{c}</div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="admin-card">
                <div className="font-editorial text-xl text-altura-espresso">Top markets</div>
                <div className="mt-4 space-y-3">
                  {(m.top_markets || []).map((r, i) => (
                    <div key={r.market} className="flex items-center justify-between border-b border-altura-espresso/5 pb-2 last:border-0">
                      <div className="text-sm text-altura-charcoal/85">{i + 1}. {r.market}</div>
                      <div className="font-mono text-xs text-altura-espresso">{r.count} leads</div>
                    </div>
                  ))}
                  {!(m.top_markets || []).length && <div className="text-xs text-altura-charcoal/50">No lead data yet.</div>}
                </div>
                <div className="mt-6 font-editorial text-xl text-altura-espresso">Lead sources</div>
                <div className="mt-4 flex flex-wrap gap-2">
                  {(m.top_sources || []).map((s) => (
                    <span key={s.source} className="badge-soft">{(sources.find((x) => x.key === s.source)?.label) || s.source}<span className="ml-2 text-altura-gold-dark">{s.count}</span></span>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
